/**
 * Utility functions for transforming trial data from backend format to frontend format
 *
 * Backend format uses snake_case and has data nested in 'meta' object
 * Frontend format uses camelCase and flat structure
 */

/**
 * Compute HH:MM:SS duration from two Unix timestamps (seconds).
 * Uses actual enter/exit times if available, falls back to scheduled times.
 */
const computeDuration = trial => {
  const start = trial.enter_datetime || trial.scheduled_start_time;
  const end = trial.exit_datetime || trial.scheduled_end_time;
  if (start == null || end == null) return 'N/A';
  const seconds = Math.max(0, Math.floor(end - start));
  const h = String(Math.floor(seconds / 3600)).padStart(2, '0');
  const m = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${h}:${m}:${s}`;
};

/**
 * Convert Unix timestamp (seconds) to YYYY-MM-DD date string.
 */
const formatTrialDate = timestamp => {
  if (timestamp == null) return '';
  return new Date(timestamp * 1000).toISOString().split('T')[0];
};

/**
 * Map backend region string to FE region code.
 * Backend sends region_data.region (e.g. "IND-SUB"), FE displays short codes.
 */
const REGION_MAP = {
  'IND-SUB': 'ISC',
  'US-CANADA': 'NAM',
  'APAC-AUS-NZ': 'APAC',
  'UK-EU-ME-AFRICA': 'EMEA',
};

const mapRegion = region => REGION_MAP[region] || region || null;

/**
 * Compute enrollment status from raw trial fields.
 * Backend provides class_date (Unix seconds), paid_on, demo_state, student_id, teacher_id.
 * Returns one of: "Yes" | "No (>2w)" | "Not yet (<2w)" | "Unknown"
 */
const computeEnrollmentStatus = trial => {
  const { demo_state, student_id, teacher_id, paid_on, class_date } = trial;
  if (demo_state === 'DONE' && student_id && teacher_id && paid_on != null) {
    return 'Yes';
  }
  if (demo_state === 'DONE' && class_date) {
    const daysSince = (Date.now() / 1000 - class_date) / 86400;
    return daysSince > 14 ? 'No (>2w)' : 'Not yet (<2w)';
  }
  return 'Unknown';
};

/**
 * Transform a single trial object from backend format to frontend format
 * @param {Object} trial - Trial data from backend (snake_case)
 * @param {number} index - Optional index for fallback ID generation
 * @returns {Object} - Transformed trial data (camelCase)
 */
export const transformSingleTrial = (trial, index = 0) => {
  if (!trial) {
    return null;
  }

  // Gateway returns flat objects with IDs at top level.
  // Note: trial.meta is the model's JSON column (is_hybrid, course, etc.) — NOT the IDs.
  let videoLinks = trial.video_url || trial.video_links;

  // Normalize to always be an array
  if (!videoLinks) {
    videoLinks = [];
  } else if (typeof videoLinks === 'string') {
    videoLinks = [videoLinks]; // Convert single string to array
  } else if (!Array.isArray(videoLinks)) {
    videoLinks = []; // If it's neither string nor array, default to empty array
  }

  return {
    id: trial.teacher_classroom_id || `unknown_${index}`,
    studentId: trial.student_id,
    tutorId: trial.teacher_id,
    teacherClassroomId: trial.teacher_classroom_id,
    studentName: trial.student_name,
    tutorName: trial.teacher_name,
    grade: trial.grade,
    subject: trial.program_type || 'N/A',
    trialDate: formatTrialDate(trial.scheduled_start_time),
    duration: computeDuration(trial),
    region: mapRegion(trial.region),
    channel: trial.channel,
    trialVersion: trial.demo_type,
    enrolled: computeEnrollmentStatus(trial),
    videoLinks: videoLinks,
  };
};

/**
 * Transform an array of trial objects from backend format to frontend format
 * @param {Array} trialArray - Array of trial data from backend
 * @returns {Array} - Array of transformed trial data
 */
const transformTrialArray = trialArray => {
  if (!Array.isArray(trialArray) || trialArray.length === 0) {
    return [];
  }

  return trialArray.map((trial, index) => transformSingleTrial(trial, index));
};

/**
 * Transform trial data (handles both single object and arrays)
 * @param {Object|Array} apiData - Trial data from API response
 * @returns {Array} - Array of transformed trial data
 */
export const transformTrialData = apiData => {
  if (!apiData) {
    return [];
  }

  if (!Array.isArray(apiData)) {
    // Try to extract array from object
    const arrayData =
      apiData.data || apiData.value || apiData.results || [apiData].filter(a => a);

    if (Array.isArray(arrayData)) {
      return transformTrialArray(arrayData);
    }
    return [];
  }

  return transformTrialArray(apiData);
};
